import type { EpistemicStatus } from '@et/schema';
import { useTranslation } from 'react-i18next';
import { DIAMOND, fillFromBottom, hatchLines, CX, CY, R } from './geometry.js';

export interface StatusMarkProps {
  readonly status: EpistemicStatus;
  readonly size?: number;
  /** Marca decorativa quando o rotulo aparece ao lado; caso contrario nomeada. */
  readonly decorative?: boolean;
  readonly className?: string;
}

/**
 * §33, §34 — o estado epistemologico e codificado em **densidade de
 * preenchimento, nao em matiz**. "Established" nao e verde e "Disputed" nao e
 * vermelho: nao sao bom e mau, sao graus de estabelecimento, e um semaforo
 * emitiria um juizo que o produto se recusa a emitir.
 *
 * `philosophical_theological` usa **losango**, nao circulo: nao e o degrau mais
 * baixo da escala, e outra categoria de afirmacao.
 */
export function StatusMark({ status, size = 22, decorative = false, className }: StatusMarkProps) {
  const { t } = useTranslation('lexicon');
  const label = t(`status.${status}`);
  const stroke = 2.2;

  const body = (() => {
    switch (status) {
      case 'established':
        return <path d={fillFromBottom(1)} fill="currentColor" />;
      case 'strongly_supported':
        return (
          <>
            <circle cx={CX} cy={CY} r={R} fill="none" stroke="currentColor" strokeWidth={stroke} />
            <path d={fillFromBottom(0.75)} fill="currentColor" />
          </>
        );
      case 'probable':
        return (
          <>
            <circle cx={CX} cy={CY} r={R} fill="none" stroke="currentColor" strokeWidth={stroke} />
            <path d={fillFromBottom(0.5)} fill="currentColor" />
          </>
        );
      case 'plausible':
        return (
          <>
            <circle cx={CX} cy={CY} r={R} fill="none" stroke="currentColor" strokeWidth={stroke} />
            <path d={fillFromBottom(0.25)} fill="currentColor" />
          </>
        );
      case 'disputed':
        return (
          <>
            <circle cx={CX} cy={CY} r={R} fill="none" stroke="currentColor" strokeWidth={stroke} />
            {hatchLines(3).map((l, i) => (
              <line key={i} {...l} stroke="currentColor" strokeWidth={1.6} />
            ))}
          </>
        );
      case 'highly_disputed':
        return (
          <>
            <circle cx={CX} cy={CY} r={R} fill="none" stroke="currentColor" strokeWidth={stroke} />
            {hatchLines(6).map((l, i) => (
              <line key={i} {...l} stroke="currentColor" strokeWidth={1.6} />
            ))}
          </>
        );
      case 'unknown':
        return (
          <circle
            cx={CX} cy={CY} r={R} fill="none" stroke="currentColor"
            strokeWidth={stroke} strokeDasharray="3 3"
          />
        );
      case 'philosophical_theological':
        return (
          <>
            <path d={DIAMOND} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinejoin="round" />
            <circle cx={CX} cy={CY} r={2.4} fill="currentColor" />
          </>
        );
    }
  })();

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 22 22"
      data-status={status}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : label}
    >
      {!decorative && <title>{label}</title>}
      {body}
    </svg>
  );
}
